import React, { useEffect, useState } from "react";
import styled from "styled-components";
import QRCode from "qrcode";
import JSZip from "jszip";
import api from "../utils/api";
import { toast } from "react-toastify";

const Page = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  text-align: center;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  }
`;

const Title = styled.h3`
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 0.5rem;
`;

const Number = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ff7a2a;
`;

const Section = styled.section`
  margin-top: 2rem;
`;

const SectionHeading = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
`;

const QrSectionCard = styled(Card)`
  grid-column: 1 / -1;
  text-align: left;
  padding: 1.5rem 2rem 2rem;
`;

const QrStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QrStat = styled.div`
  background: #fff7f0;
  border: 1px solid #ffe0c7;
  border-radius: 10px;
  padding: 1.25rem 1rem;
  text-align: center;
`;

const QrStatLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.35rem;
`;

const QrStatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ff7a2a;
`;

const QrHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const QrTotal = styled.p`
  margin: 0.35rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
`;

const Button = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1.25rem;
  background: #ff7a2a;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #e66a1a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  min-width: 320px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h4`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: #ff7a2a;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalEntries: 0,
    totalqrcodes: 0,
    qrGenerated: 0,
    qrAssigned: 0,
    qrBalanced: 0,
    totalRoutes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [qrCount, setQrCount] = useState("");
  const [generating, setGenerating] = useState(false);

  const mapStats = (payload = {}) => {
    const totalUsers = payload.totalUsers ?? payload.totalSurveyors ?? 0;
    const totalEntries = payload.totalEntries ?? 0;
    const totalqrcodes = payload.totalqrcodes ?? 0;
    const qrGenerated = payload.qrGenerated ?? totalqrcodes ?? 0;
    const qrAssigned = payload.qrAssigned ?? 0;
    const qrBalanced = payload.qrBalanced ?? 0;
    const totalRoutes = payload.totalRoutes ?? 0;

    return {
      totalUsers,
      totalEntries,
      totalqrcodes,
      qrGenerated,
      qrAssigned,
      qrBalanced,
      totalRoutes,
    };
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await api.get("/api/dataanalysis/stats", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const payload = res.data?.data || {};
        setStats(mapStats(payload));
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const openModal = () => {
    setQrCount("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!generating) setModalOpen(false);
  };

  const handleGenerate = async () => {
    const num = parseInt(qrCount, 10);
    if (isNaN(num) || num < 1 || num > 500) {
      toast.error("Please enter a number between 1 and 500.");
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post("/api/v1/qrcodes/generate", { count: num });
      const data = res.data;

      if (!data.success || !Array.isArray(data.qr_codes)) {
        toast.error(data.message || "Failed to generate QR codes.");
        return;
      }

      const qrCodes = data.qr_codes;
      const zip = new JSZip();

      for (let i = 0; i < qrCodes.length; i++) {
        const text = qrCodes[i];
        const dataUrl = await QRCode.toDataURL(text, {
          type: "image/png",
          margin: 2,
          width: 256,
        });
        const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, "");
        zip.file(`${text}.png`, base64, { base64: true });
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-codes-${qrCodes.length}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(data.message || `Downloaded ${qrCodes.length} QR code(s).`);
      setModalOpen(false);
      setQrCount("");

      const statsRes = await api.get("/api/dataanalysis/stats", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setStats(mapStats(statsRes.data?.data || {}));
    } catch (err) {
      console.error("Error generating QR codes:", err);
      toast.error(
        err.response?.data?.message || "Failed to generate QR codes.",
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Page>
      <h1 className="text-2xl font-semibold mb-6 text-gray-800">Dashboard</h1>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <Grid>
            <Card>
              <Title>Total Registered Surveyors</Title>
              <Number>{stats.totalUsers}</Number>
            </Card>
            <Card>
              <Title>Total Entries Submitted</Title>
              <Number>{stats.totalEntries}</Number>
            </Card>
            <Card>
              <Title>Total Routes</Title>
              <Number>{stats.totalRoutes}</Number>
            </Card>
          </Grid>

          <Section>
            <QrSectionCard>
              <QrHeader>
                <div>
                  <SectionHeading>QR Codes</SectionHeading>
                  <QrTotal>
                    Total QR codes: {stats.totalqrcodes || stats.qrGenerated}
                  </QrTotal>
                </div>
                <Button type="button" onClick={openModal}>
                  Generate
                </Button>
              </QrHeader>
              <QrStatsGrid>
                <QrStat>
                  <QrStatLabel>Generated</QrStatLabel>
                  <QrStatValue>
                    {stats.qrGenerated || stats.totalqrcodes}
                  </QrStatValue>
                </QrStat>
                <QrStat>
                  <QrStatLabel>Assigned</QrStatLabel>
                  <QrStatValue>{stats.qrAssigned}</QrStatValue>
                </QrStat>
                <QrStat>
                  <QrStatLabel>Balanced</QrStatLabel>
                  <QrStatValue>{stats.qrBalanced}</QrStatValue>
                </QrStat>
              </QrStatsGrid>
            </QrSectionCard>
          </Section>
        </>
      )}

      {modalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Generate QR codes</ModalTitle>
            <p
              style={{
                margin: "0 0 0.5rem 0",
                color: "#666",
                fontSize: "0.9rem",
              }}
            >
              How many QR codes do you want to generate? (1–500)
            </p>
            <Input
              type="number"
              min={1}
              max={500}
              placeholder="Enter number (1–500)"
              value={qrCount}
              onChange={(e) => setQrCount(e.target.value)}
              disabled={generating}
            />
            <ModalActions>
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? "Generating…" : "Generate"}
              </Button>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default Dashboard;
